import { useEffect, useCallback, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { useModalStore } from '@/stores/modal-store';
import { useWorkspaceStore, getAllPanes } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useToastStore } from '@/stores/toast-store';
import { useStateSync } from '@/hooks/useStateSync';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { ResizeHandle } from './ResizeHandle';
import { Sidebar } from '@/components/sidebar/Sidebar';
import { CollapsedStrip } from '@/components/sidebar/CollapsedStrip';
import { ConnectionModal } from '@/components/modals/ConnectionModal';
import { TunnelModal } from '@/components/modals/TunnelModal';
import { DatabaseModal } from '@/components/modals/DatabaseModal';
import { SettingsModal } from '@/components/modals/SettingsModal';
import { ShareDialog } from '@/components/modals/ShareDialog';
import { FileUploadModal } from '@/components/modals/FileUploadModal';
import { ConfirmDialogHost } from '@/components/shared/ConfirmDialogHost';
import { Workarea } from '@/components/workarea/Workarea';
import { BottomPanel } from '@/components/bottom-panel/BottomPanel';
import { CommandBar } from '@/components/shared/CommandBar';
import { bootstrapCommands } from '@/lib/commands';

// Register every command provider exactly once. Safe to call from module
// scope — the bootstrap is idempotent.
bootstrapCommands();

const SIDEBAR_MIN = 200;
const SIDEBAR_MAX = 400;
const BOTTOM_MIN = 100;
const BOTTOM_MAX = 400;

export function AppShell() {
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const bottomPanelCollapsed = useAppStore((s) => s.bottomPanelCollapsed);
  const sidebarWidth = useAppStore((s) => s.sidebarWidth);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const toggleBottomPanel = useAppStore((s) => s.toggleBottomPanel);
  const setSidebarWidth = useAppStore((s) => s.setSidebarWidth);
  const setBottomPanelHeight = useAppStore((s) => s.setBottomPanelHeight);
  const bottomPanelHeight = useAppStore((s) => s.bottomPanelHeight);
  const theme = useAppStore((s) => s.theme);

  // Apply theme to document element whenever it changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Ensure the admin-shell tab exists + is pinned for admin users — but
  // only when the feature is actually enabled. Singleton id `admin-shell`
  // makes this idempotent — re-mounting AppShell (e.g., theme change,
  // login refresh) won't add a second one.
  const { data: me } = useQuery<{ role?: string }>({
    queryKey: ['me'],
    queryFn: () => apiClient.get('/api/auth/me'),
    staleTime: 60_000,
  });
  // Empty tmux session name disables the admin shell server-side (see
  // ws/admin-shell.ts), so the tab must follow the same gate or it'd just
  // error on connect. Default mirrors SettingsModal's 'dbgo'.
  const adminShellSession = useSettingsStore((s) => s.settings['server.adminShellTmuxSession'] ?? 'dbgo');
  const settingsLoaded = useSettingsStore((s) => s.loaded);
  useEffect(() => {
    // Wait for settings so we don't churn the tab while the value is
    // momentarily unknown on first paint.
    if (!settingsLoaded) return;

    const ws = useWorkspaceStore.getState();
    const appState = useAppStore.getState();
    const exists = getAllPanes(ws.root).some((p) => p.tabs.some((t) => t.id === 'admin-shell'));
    const enabled = me?.role === 'admin' && adminShellSession.trim() !== '';

    if (enabled) {
      if (!exists) {
        ws.openTab({ type: 'admin-shell', id: 'admin-shell', title: 'Admin Shell' });
      }
      // Pin (idempotent: togglePinTab toggles, so only call if not already pinned)
      if (!appState.tabPins['admin-shell']) {
        appState.togglePinTab('admin-shell');
      }
    } else if (exists) {
      // Disabled (empty session) or no longer admin — tear down the tab
      // and drop its pin so it stops being persisted in the workspace.
      ws.closeTab('admin-shell');
      if (appState.tabPins['admin-shell']) {
        appState.togglePinTab('admin-shell');
      }
    }
  }, [me?.role, adminShellSession, settingsLoaded]);

  // Modal state lives in useModalStore so any component can trigger New/Edit
  // for connections and tunnels (InfoView's Actions panel, mobile screens,
  // command palette, etc.), while the singleton modals still live at the root.
  const connectionModal = useModalStore((s) => s.connectionModal);
  const tunnelModal = useModalStore((s) => s.tunnelModal);
  const databaseModal = useModalStore((s) => s.databaseModal);
  const openNewConnection = useModalStore((s) => s.openNewConnection);
  const openEditConnection = useModalStore((s) => s.openEditConnection);
  const closeConnectionModal = useModalStore((s) => s.closeConnectionModal);
  const openNewTunnel = useModalStore((s) => s.openNewTunnel);
  const openEditTunnel = useModalStore((s) => s.openEditTunnel);
  const closeTunnelModal = useModalStore((s) => s.closeTunnelModal);
  const closeDatabaseModal = useModalStore((s) => s.closeDatabaseModal);
  const settingsModal = useModalStore((s) => s.settingsModal);
  const closeSettingsModal = useModalStore((s) => s.closeSettings);
  const openSettings = useModalStore((s) => s.openSettings);
  const shareDialog = useModalStore((s) => s.shareDialog);
  const closeShareDialog = useModalStore((s) => s.closeShareDialog);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  // Command bar state. `initialInput` is `>` for Cmd+Shift+P (command mode)
  // and empty for Cmd+P (search mode). A `dbgo:command-bar:open` custom
  // event also drives this so commands can chain into the bar (e.g.,
  // "Connect to Database…" reopens it with `@connection` pre-filled).
  const [commandBar, setCommandBar] = useState<{ open: boolean; initial: string }>({
    open: false,
    initial: '',
  });
  const queryClient = useQueryClient();

  // Persist state changes to server
  useStateSync();

  // Helper: open a new query tab for the first connected connection
  const openNewQueryTab = useCallback(() => {
    const { activeConnections } = useConnectionStore.getState();
    const connectedId = Object.keys(activeConnections).find(
      (id) => activeConnections[id].status === 'connected'
    );
    if (!connectedId) {
      useToastStore.getState().addToast('warning', 'No active connection. Connect to a database first.');
      return;
    }
    const tabId = `query-${Date.now()}`;
    useWorkspaceStore.getState().openTab({
      type: 'query',
      id: tabId,
      connectionId: connectedId,
      title: 'New Query',
      sql: '',
    });
  }, []);

  // Helper: close active tab
  const closeActiveTab = useCallback(() => {
    const { activeTabId, closeTab } = useWorkspaceStore.getState();
    if (activeTabId) closeTab(activeTabId);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;

      // Cmd+Shift+P: command palette (starts with `>` → command mode)
      if (mod && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        e.preventDefault();
        setCommandBar((s) => (s.open ? { open: false, initial: '' } : { open: true, initial: '> ' }));
        return;
      }

      // Cmd+P: global search (empty input → search mode). We special-case
      // this ahead of the browser's Print shortcut.
      if (mod && !e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setCommandBar((s) => (s.open ? { open: false, initial: '' } : { open: true, initial: '' }));
        return;
      }

      if (mod && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
      if (mod && e.key === 'j') {
        e.preventDefault();
        toggleBottomPanel();
      }

      // Ctrl+T: New query tab
      if (mod && e.key === 't') {
        e.preventDefault();
        openNewQueryTab();
      }

      // Ctrl+W: Close active tab
      if (mod && e.key === 'w') {
        e.preventDefault();
        closeActiveTab();
      }

      // Ctrl+Tab / Ctrl+Shift+Tab: Next/prev tab
      if (e.ctrlKey && e.key === 'Tab') {
        e.preventDefault();
        const { tabs, activeTabId, switchTab } = useWorkspaceStore.getState();
        if (tabs.length < 2) return;
        const idx = tabs.findIndex((t) => t.id === activeTabId);
        const next = e.shiftKey
          ? (idx - 1 + tabs.length) % tabs.length
          : (idx + 1) % tabs.length;
        switchTab(tabs[next].id);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar, toggleBottomPanel, openNewQueryTab, closeActiveTab]);

  // Custom events so commands can reach app-shell-level UI:
  //   dbgo:command-bar:open — reopen the bar with a prefill (scope chaining)
  //   dbgo:files:upload     — `> Upload Files` command
  useEffect(() => {
    function handleOpenBar(e: Event) {
      const detail = (e as CustomEvent<{ prefill?: string }>).detail ?? {};
      setCommandBar({ open: true, initial: detail.prefill ?? '' });
    }
    function handleFilesUpload() {
      setShowFileUploadModal(true);
    }
    window.addEventListener('dbgo:command-bar:open', handleOpenBar);
    window.addEventListener('dbgo:files:upload', handleFilesUpload);
    return () => {
      window.removeEventListener('dbgo:command-bar:open', handleOpenBar);
      window.removeEventListener('dbgo:files:upload', handleFilesUpload);
    };
  }, []);

  const handleSidebarResize = useCallback((delta: number) => {
    const current = useAppStore.getState().sidebarWidth;
    setSidebarWidth(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, current + delta)));
  }, [setSidebarWidth]);

  const handleBottomResize = useCallback((delta: number) => {
    const current = useAppStore.getState().bottomPanelHeight;
    setBottomPanelHeight(Math.min(BOTTOM_MAX, Math.max(BOTTOM_MIN, current - delta)));
  }, [setBottomPanelHeight]);

  const effectiveSidebarWidth = sidebarCollapsed ? 40 : sidebarWidth;
  const effectiveBottomHeight = bottomPanelCollapsed ? 30 : bottomPanelHeight;

  return (
    <div className="h-screen w-screen bg-dbgo-root flex flex-col overflow-hidden pwa-safe-area">
      {/* Top area: sidebar + workarea */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div
          className="flex-shrink-0 bg-dbgo-sidebar border-r transition-[width] duration-[280ms] ease-out-custom overflow-hidden"
          style={{ width: effectiveSidebarWidth }}
        >
          {sidebarCollapsed ? (
            <CollapsedStrip onExpand={toggleSidebar} />
          ) : (
            <Sidebar
              onNewConnection={openNewConnection}
              onEditConnection={openEditConnection}
              onNewTunnel={openNewTunnel}
              onEditTunnel={openEditTunnel}
              onUploadFiles={() => setShowFileUploadModal(true)}
              onOpenSettings={() => openSettings()}
            />
          )}
        </div>

        {/* Resize handle: sidebar <-> workarea */}
        {!sidebarCollapsed && (
          <ResizeHandle direction="vertical" onResize={handleSidebarResize} />
        )}

        {/* Workarea */}
        <Workarea />
      </div>

      {/* Resize handle: workarea <-> bottom panel */}
      {!bottomPanelCollapsed && (
        <ResizeHandle direction="horizontal" onResize={handleBottomResize} />
      )}

      {/* Bottom panel */}
      <BottomPanel
        collapsed={bottomPanelCollapsed}
        onToggle={toggleBottomPanel}
        height={effectiveBottomHeight}
      />

      {/* Connection modal — shared between new and edit */}
      {connectionModal.mode !== 'closed' && (
        <ConnectionModal
          connectionId={connectionModal.mode === 'edit' ? connectionModal.connection.id : undefined}
          initialData={connectionModal.mode === 'edit' ? connectionModal.connection : undefined}
          onClose={closeConnectionModal}
          onSaved={() => {
            closeConnectionModal();
            queryClient.invalidateQueries({ queryKey: ['connections'] });
          }}
        />
      )}

      {/* Tunnel modal — shared between new and edit */}
      {tunnelModal.mode !== 'closed' && (
        <TunnelModal
          tunnelId={tunnelModal.mode === 'edit' ? tunnelModal.tunnel.id : undefined}
          initialData={tunnelModal.mode === 'edit' ? tunnelModal.tunnel : undefined}
          onClose={closeTunnelModal}
          onSaved={() => {
            closeTunnelModal();
            queryClient.invalidateQueries({ queryKey: ['tunnels'] });
          }}
        />
      )}

      {/* Database modal — create or edit */}
      {databaseModal.mode !== 'closed' && (
        <DatabaseModal
          connectionId={databaseModal.connectionId}
          mode={databaseModal.mode}
          currentName={databaseModal.mode === 'edit' ? databaseModal.name : undefined}
          onClose={closeDatabaseModal}
          onSaved={() => {
            closeDatabaseModal();
            queryClient.invalidateQueries({ queryKey: ['schema', databaseModal.connectionId] });
          }}
        />
      )}

      {/* Share dialog — for connections + tunnels. Singleton at the shell. */}
      {shareDialog.mode === 'open' && (
        <ShareDialog
          resourceType={shareDialog.resourceType}
          resourceId={shareDialog.resourceId}
          resourceName={shareDialog.resourceName}
          onClose={closeShareDialog}
        />
      )}

      {/* Imperative destructive-confirm dialog (useDialogStore.showConfirm) */}
      <ConfirmDialogHost />

      {/* Settings modal — driven by modal-store so commands can open it */}
      {settingsModal.mode === 'open' && (
        <SettingsModal onClose={closeSettingsModal} />
      )}

      {/* File upload modal */}
      {showFileUploadModal && (
        <FileUploadModal
          onClose={() => setShowFileUploadModal(false)}
          onCreated={() => {
            setShowFileUploadModal(false);
            queryClient.invalidateQueries({ queryKey: ['connections'] });
          }}
        />
      )}

      {/* Unified command bar: Cmd+P (search) and Cmd+Shift+P (commands, `>` prefilled) */}
      <CommandBar
        open={commandBar.open}
        initialInput={commandBar.initial}
        onClose={() => setCommandBar({ open: false, initial: '' })}
      />
    </div>
  );
}
